import type { FieldErrors } from "react-hook-form";
import { z } from "zod";
import { isBefore, isValid } from "date-fns";
import { EducationLevel } from "src/__generated__/graphql";
import { tertiaryQualifications } from "src/features/pro-portfolio/constants";

const PAST_DATE_ERROR_MESSAGE = "Date must be today or in the past";
const QUALIFICATION_REQUIRED_MESSAGE = "Qualification is required";
const FIELD_OF_STUDY_REQUIRED_MESSAGE =
	"Field of study is required for the selected qualification";

const emptyStringToUndefined = (value: unknown) => {
	if (typeof value !== "string") {
		return value;
	}

	const trimmedValue = value.trim();

	return trimmedValue === "" ? undefined : trimmedValue;
};

const isPastDate = (value: string) => {
	const date = new Date(value);

	return isValid(date) && isBefore(date, new Date());
};

const requiredPastDateString = (requiredMessage = "Date is required") =>
	z.string().superRefine((value, ctx) => {
		if (!value) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: requiredMessage,
			});
			return;
		}

		if (!isPastDate(value)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: PAST_DATE_ERROR_MESSAGE,
			});
		}
	});

const optionalPastDateString = z
	.preprocess(emptyStringToUndefined, z.string().optional())
	.superRefine((value, ctx) => {
		if (!value) {
			return;
		}

		if (!isPastDate(value)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: PAST_DATE_ERROR_MESSAGE,
			});
		}
	});

const optionalString = z.preprocess(
	emptyStringToUndefined,
	z.string().trim().optional()
);

export const educationQualificationSchema = z.nativeEnum(EducationLevel, {
	required_error: QUALIFICATION_REQUIRED_MESSAGE,
	invalid_type_error: QUALIFICATION_REQUIRED_MESSAGE,
});

export const manualEducationQualificationSchema = z.preprocess(
	(value) => (value === "" ? undefined : value),
	educationQualificationSchema
);

const educationFieldShape = {
	schoolName: z
		.string()
		.trim()
		.min(3, "School name is required and must be at least 3 characters"),
	fieldOfStudy: optionalString,
};

const addEducationFieldOfStudyRequirement = (
	data: { qualification: EducationLevel; fieldOfStudy?: string },
	ctx: z.RefinementCtx
) => {
	if (
		tertiaryQualifications.includes(data.qualification) &&
		!data.fieldOfStudy
	) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: FIELD_OF_STUDY_REQUIRED_MESSAGE,
			path: ["fieldOfStudy"],
		});
	}
};

export const prefillEducationItemSchema = z
	.object({
		...educationFieldShape,
		qualification: educationQualificationSchema,
	})
	.superRefine((data, ctx) => {
		addEducationFieldOfStudyRequirement(data, ctx);
	});

export const manualEducationSchema = z
	.object({
		...educationFieldShape,
		qualification: manualEducationQualificationSchema,
	})
	.superRefine((data, ctx) => {
		addEducationFieldOfStudyRequirement(data, ctx);
	});

export const educationEditorSchema = z
	.object({
		...educationFieldShape,
		qualification: educationQualificationSchema,
	})
	.superRefine((data, ctx) => {
		addEducationFieldOfStudyRequirement(data, ctx);
	});

export const prefillEmploymentItemSchema = z.object({
	companyName: z.string().trim().min(3, "Company name is required"),
	jobTitle: z.string().trim().min(3, "Job title is required"),
	description: z.string().trim().min(10, "Job description is required"),
	startedAt: requiredPastDateString("Start date is required"),
	endedAt: optionalPastDateString,
});

export const manualEmploymentSchema = z.object({
	companyName: z.string().trim().min(3, "Company name is required"),
	jobTitle: z.string().trim().min(3, "Job title is required"),
	jobDescription: z.string().trim().min(10, "Job description is required"),
	startDate: requiredPastDateString("Start date is required"),
	endDate: optionalPastDateString,
});

export const employmentEditorSchema = z.object({
	companyName: z.string().trim().min(3, "Company name is required"),
	jobTitle: z.string().trim().min(3, "Job title is required"),
	description: z.string().trim().min(
		10,
		"Description must be at least 10 characters"
	),
	startDate: requiredPastDateString("Start date is required"),
	endDate: optionalPastDateString,
});

export const prefillProjectHistoryItemSchema = z.object({
	name: z.string().trim().min(3, "Project name is required"),
	description: z.string().trim().min(10, "Project description is required"),
	startedAt: optionalString,
	endedAt: requiredPastDateString("Completion date is required"),
});

export const manualProjectSchema = z.object({
	taskName: z.string().trim().min(3, "Project name is required"),
	taskDescription: z.string().trim().min(10, "Project description is required"),
	date: requiredPastDateString("Completion date is required"),
});

export const projectEditorSchema = manualProjectSchema;

export const requiredCertificationSchema = z.object({
	name: z.string().trim().min(3, "Certification name is required"),
	issuedBy: z.string().trim().min(3, "Issuer is required"),
	issuerCertificationId: optionalString,
	issuedAt: requiredPastDateString(),
});

export const prefillCertificationItemSchema = requiredCertificationSchema.extend({
	url: optionalString,
});

export const portfolioInputReviewSchema = z.object({
	professionalSummary: z.string(),
	educationItems: z.array(prefillEducationItemSchema),
	employmentItems: z.array(prefillEmploymentItemSchema),
	workHistoryItems: z.array(prefillProjectHistoryItemSchema),
	certificationItems: z.array(prefillCertificationItemSchema),
});

export type PortfolioInputReviewFormValues = z.infer<
	typeof portfolioInputReviewSchema
>;

export interface PortfolioInputSectionSummary {
	section: string;
	count: number;
}

const countLeafErrors = (value: unknown): number => {
	if (!value) {
		return 0;
	}

	if (Array.isArray(value)) {
		return value.reduce((total, item) => total + countLeafErrors(item), 0);
	}

	if (typeof value === "object") {
		if ("message" in value && typeof value.message === "string") {
			return 1;
		}

		return Object.values(value).reduce(
			(total, item) => total + countLeafErrors(item),
			0
		);
	}

	return 0;
};

export const getPortfolioInputSectionSummaries = (
	errors: FieldErrors<PortfolioInputReviewFormValues>
): PortfolioInputSectionSummary[] =>
	[
		{
			section: "Employment history",
			count: countLeafErrors(errors.employmentItems),
		},
		{
			section: "Project history",
			count: countLeafErrors(errors.workHistoryItems),
		},
		{
			section: "Education",
			count: countLeafErrors(errors.educationItems),
		},
		{
			section: "Certifications",
			count: countLeafErrors(errors.certificationItems),
		},
	].filter((summary) => summary.count > 0);

export const getPortfolioInputIssueCount = (
	errors: FieldErrors<PortfolioInputReviewFormValues>
) =>
	getPortfolioInputSectionSummaries(errors).reduce(
		(total, summary) => total + summary.count,
		0
	);
